// /////////////////////////////////////////////////////////////
// [ファイル] useProfitAndLossReview.ts
// [概要] 経営概況詳細 (損益) ページの「AI 評価分析コメント」を取得するカスタムフック。
//        画面で算出済みの業績サマリー・拠点別/部署別損益・異常科目を要約 JSON 化し、
//        Node 関数 plReview へ ID トークン付きで POST して評価テキスト (Markdown) を得る。
//        拠点別・部署別のマトリクス表は数値転記の誤りを避けるため AI に書かせず、
//        AI が置いたプレースホルダーをここで算出した表に差し替える。
//        会話履歴・Firestore は使わないワンショット呼び出し。
// /////////////////////////////////////////////////////////////
import { useState, useCallback } from 'react';
import axios from 'axios';
import { getIdToken } from '../firebase/getIdToken';
import {
  getTargetMonths,
  calculateCurrentFiscalYear,
  fmtMillion,
  MONTH_ORDER,
  PERFORMANCE_RANGE_OPTIONS,
  type PerformanceRange,
  type TrialBalanceRow,
  type DepartmentPlRow,
  type BranchPlRow,
  type StaffHeadcountRow,
} from '../components/profitAndLoss/constants';

// Node 関数 (Cloud Functions) は ep() の移行対象外のため完全 URL を直接指定する
const PL_REVIEW_URL =
  'https://asia-northeast1-plexiform-bot-455507-h3.cloudfunctions.net/plReview';

// AI 出力中のマトリクス表プレースホルダー (functions-node の PL_REVIEW_SYSTEM_PROMPT と一致させること)
const MATRIX_SLOTS = [
  { key: 'branch', heading: '拠点別', placeholder: '{{BRANCH_MATRIX}}' },
  { key: 'department', heading: '部署別', placeholder: '{{DEPARTMENT_MATRIX}}' },
] as const;
type SalesMatrices = Record<(typeof MATRIX_SLOTS)[number]['key'], string | null>;

// 評価対象となる画面状態のスナップショット (生成ボタン押下時点の値)
export interface PlReviewInput {
  dedupedRows: TrialBalanceRow[];
  deptPlRows: DepartmentPlRow[];
  branchPlRows: BranchPlRow[];
  staffRows: StaffHeadcountRow[];
  performanceRange: PerformanceRange;
  currentMonth: number;
  fiscalYear: number;
  // 採用計画 予実用 (会計月→計画採用人数、予算バージョン名)。未指定なら予実は評価しない
  planHiresByMonth?: Record<number, number>;
  planVersionName?: string;
}

// 予算達成率 (実績 / |予算| × 100)。予算 0 のときは null
const achievementRate = (actual: number, budget: number): number | null =>
  budget === 0 ? null : Number(((actual / Math.abs(budget)) * 100).toFixed(1));
// 前年比 (実績 / |前年| × 100)。前年 0 のときは null
const yoyRate = (actual: number, prev: number): number | null =>
  prev === 0 ? null : Number(((actual / Math.abs(prev)) * 100).toFixed(1));

// 業績範囲のラベル (単月は「N月」)
const rangeLabel = (range: PerformanceRange, currentMonth: number): string =>
  range === 'month'
    ? `${currentMonth}月`
    : PERFORMANCE_RANGE_OPTIONS.find((o) => o.value === range)?.label ?? '通期';

// 売上の1単位 (拠点 or 部署) の評価用集計値
type SalesUnit = {
  name: string;
  salesActual: number;
  salesBudget: number;
  salesPrev: number;
  achievementRate: number | null;
  yoyRate: number | null;
  planVsPrevRate: number | null;
  salesShare: number | null;
};

// マトリクス表の金額セル (百万円)。丸めると 0.0 になる小さな負値は「-0.0」を避けて 0.0 にする
const fmtCellMillion = (v: number): string => fmtMillion(Math.abs(v) < 50000 ? 0 : v);
const fmtCellRate = (v: number | null): string => (v === null ? '—' : `${v.toFixed(1)}%`);

/**
 * 拠点別・部署別の売上高マトリクス表 (Markdown)。行は API の並び順 (マスタ順) のまま、末尾に合計行を付ける。
 * 行が無いときは null (AI 側も見出しを出さない)。
 */
function buildSalesMatrix(label: string, units: SalesUnit[]): string | null {
  if (units.length === 0) return null;
  const total = units.reduce(
    (t, u) => ({ actual: t.actual + u.salesActual, budget: t.budget + u.salesBudget, prev: t.prev + u.salesPrev }),
    { actual: 0, budget: 0, prev: 0 },
  );
  const row = (cells: string[]) => `| ${cells.join(' | ')} |`;
  return [
    '売上高 (単位 百万円)',
    '',
    row([label, '売上実績', '予算', '達成率', '前年実績', '前年比', '構成比']),
    '|:--|--:|--:|--:|--:|--:|--:|',
    ...units.map((u) =>
      row([
        u.name,
        fmtCellMillion(u.salesActual),
        fmtCellMillion(u.salesBudget),
        fmtCellRate(u.achievementRate),
        fmtCellMillion(u.salesPrev),
        fmtCellRate(u.yoyRate),
        fmtCellRate(u.salesShare),
      ]),
    ),
    row([
      '**合計**',
      `**${fmtCellMillion(total.actual)}**`,
      `**${fmtCellMillion(total.budget)}**`,
      `**${fmtCellRate(achievementRate(total.actual, total.budget))}**`,
      `**${fmtCellMillion(total.prev)}**`,
      `**${fmtCellRate(yoyRate(total.actual, total.prev))}**`,
      `**${fmtCellRate(total.actual === 0 ? null : 100)}**`,
    ]),
  ].join('\n');
}

/**
 * AI 出力中のプレースホルダーをマトリクス表に差し替える。
 * AI がプレースホルダーを書き落とした場合は該当見出しの直後に差し込み、見出しも無ければそのままにする。
 */
function injectSalesMatrices(reply: string, matrices: SalesMatrices): string {
  let out = reply;
  for (const slot of MATRIX_SLOTS) {
    const table = matrices[slot.key];
    const escaped = slot.placeholder.replace(/[{}]/g, '\\$&');
    // コード装飾 (`{{...}}`) 付きで出力されても表として描画されるよう、前後のバッククォートごと置換する
    if (out.includes(slot.placeholder)) {
      out = out.replace(new RegExp(`\`?${escaped}\`?`, 'g'), () => (table ? `\n\n${table}\n\n` : ''));
      continue;
    }
    if (!table) continue;
    const headingRe = new RegExp(`^##\\s*${slot.heading}\\s*$`, 'm');
    out = out.replace(headingRe, (h) => `${h}\n\n${table}\n\n`);
  }
  return out.replace(/\n{3,}/g, '\n\n');
}

/**
 * 画面状態から plReview へ送る損益サマリー JSON を組み立てる。
 * PerformanceSummaryCards / DepartmentPerformanceCards と同じ集計ロジックで算出し、
 * AI が画面表示と矛盾しない評価を返せるようにする。
 * 併せて、実績評価モードで AI 出力へ差し込む拠点別・部署別のマトリクス表を返す。
 */
function buildSummary(input: PlReviewInput): { summary: Record<string, unknown>; matrices: SalesMatrices } {
  const { dedupedRows, deptPlRows, branchPlRows, staffRows, performanceRange, currentMonth, fiscalYear } = input;
  const targetMonths = getTargetMonths(performanceRange, currentMonth);

  // 対象月分の合計を返す (PerformanceSummaryCards と同じ category_l2_name 集計)
  const getYearTotal = (
    category: string,
    suffix: 'actual' | 'budget' | 'actual_prev' = 'actual',
  ): number =>
    dedupedRows
      .filter((r) => r.category_l2_name === category)
      .reduce(
        (sum, r) =>
          sum +
          targetMonths.reduce((msum, m) => msum + (Number(r[`month_${m}_${suffix}`]) || 0), 0),
        0,
      );

  // ① 全社業績サマリー (4 指標)
  const sales = { actual: getYearTotal('純売上高'), budget: getYearTotal('純売上高', 'budget'), prev: getYearTotal('純売上高', 'actual_prev') };
  const cogs = { actual: getYearTotal('完成工事原価'), budget: getYearTotal('完成工事原価', 'budget'), prev: getYearTotal('完成工事原価', 'actual_prev') };
  const sga = { actual: getYearTotal('販売費及び一般管理費計'), budget: getYearTotal('販売費及び一般管理費計', 'budget'), prev: getYearTotal('販売費及び一般管理費計', 'actual_prev') };
  const gross = { actual: sales.actual - cogs.actual, budget: sales.budget - cogs.budget, prev: sales.prev - cogs.prev };
  const op = { actual: gross.actual - sga.actual, budget: gross.budget - sga.budget, prev: gross.prev - sga.prev };

  const metric = (label: string, v: { actual: number; budget: number; prev: number }) => ({
    label,
    actual: Math.round(v.actual),
    budget: Math.round(v.budget),
    prev: Math.round(v.prev),
    achievementRate: achievementRate(v.actual, v.budget),
    yoyRate: yoyRate(v.actual, v.prev),
    // 計画(予算)の対前年比。計画分析モードで成長計画の評価に使う
    planVsPrevRate: yoyRate(v.budget, v.prev),
  });

  const companySummary = [
    metric('売上高', sales),
    metric('売上総利益', gross),
    metric('販管費', sga),
    metric('営業利益', op),
  ];

  // 評価モードの決定。
  // ・未到来の決算期 (来期以降) は、受注残の繰越計上や概算仕訳の起票で実績が一部計上されていても、
  //   期が到来していないため実績評価の対象外とし、計画分析モードに固定する。
  // ・当期以前は従来どおり、実績が1円でもあれば実績評価、実績ゼロ (予算のみ) なら計画分析。
  const isFuturePeriod = fiscalYear > calculateCurrentFiscalYear();
  const hasActual = sales.actual !== 0 || cogs.actual !== 0 || sga.actual !== 0;
  const hasBudget = sales.budget !== 0 || cogs.budget !== 0 || sga.budget !== 0;
  const mode: 'actual' | 'plan' = isFuturePeriod || (!hasActual && hasBudget) ? 'plan' : 'actual';

  // 計画(予算)ベースの利益構造比率。計画分析モードで主に評価する
  const pct = (num: number, den: number): number | null =>
    den === 0 ? null : Number(((num / den) * 100).toFixed(1));
  const planRatios = {
    grossMarginRate: pct(gross.budget, sales.budget), // 売上総利益率(計画)
    sgaRate: pct(sga.budget, sales.budget), // 販管費率(計画)
    opMarginRate: pct(op.budget, sales.budget), // 営業利益率(計画)
  };

  // ② 拠点別・部署別損益 (売上高の実績/予算/前年。月次内訳を範囲で合算)
  const sumSales = (d: DepartmentPlRow | BranchPlRow, suffix: 'actual' | 'budget' | 'prev'): number => {
    const byMonth =
      suffix === 'actual' ? d.sales_actual_by_month : suffix === 'budget' ? d.sales_budget_by_month : d.sales_prev_by_month;
    if (!byMonth) {
      const fallback = suffix === 'actual' ? d.sales_actual : suffix === 'budget' ? d.sales_budget : d.sales_prev;
      return performanceRange === 'annual' ? fallback : 0;
    }
    return targetMonths.reduce((s, m) => s + (Number(byMonth[m]) || 0), 0);
  };
  // 構成比の分母は絞り込み前の全行合計 (実績モードは実績、計画モードは予算)。
  // 計画分析モードは予算ゼロの行、実績モードは実績・予算・前年がすべてゼロの行を除外する (並びは API のマスタ順)
  const toSalesUnits = (rows: { name: string; row: DepartmentPlRow | BranchPlRow }[]): SalesUnit[] => {
    const units = rows.map(({ name, row }) => {
      const actual = sumSales(row, 'actual');
      const budget = sumSales(row, 'budget');
      const prev = sumSales(row, 'prev');
      return {
        name,
        salesActual: Math.round(actual) || 0,
        salesBudget: Math.round(budget) || 0,
        salesPrev: Math.round(prev) || 0,
        achievementRate: achievementRate(actual, budget),
        yoyRate: yoyRate(actual, prev),
        // 計画売上の対前年比 (計画分析モードで拠点別・部署別の成長計画を見る)
        planVsPrevRate: yoyRate(budget, prev),
        salesShare: null as number | null,
      };
    });
    const shareBase = units.reduce((s, u) => s + (mode === 'plan' ? u.salesBudget : u.salesActual), 0);
    return units
      .map((u) => ({ ...u, salesShare: pct(mode === 'plan' ? u.salesBudget : u.salesActual, shareBase) }))
      .filter((u) =>
        mode === 'plan' ? u.salesBudget !== 0 : u.salesActual !== 0 || u.salesBudget !== 0 || u.salesPrev !== 0,
      );
  };
  // AI へは規模の大きい順に渡す (計画分析モードは予算、実績モードは実績の絶対値順)
  const bySize = (a: SalesUnit, b: SalesUnit) =>
    mode === 'plan'
      ? Math.abs(b.salesBudget) - Math.abs(a.salesBudget)
      : Math.abs(b.salesActual) - Math.abs(a.salesActual);
  const branchUnits = toSalesUnits(branchPlRows.map((b) => ({ name: b.branch_name, row: b })));
  const departmentUnits = toSalesUnits(deptPlRows.map((d) => ({ name: d.department_name, row: d })));
  const branches = [...branchUnits].sort(bySize);
  const departments = [...departmentUnits].sort(bySize);

  // ③ 異常科目 (勘定科目単位で対象範囲を合算し、予算差・前年差の大きい順に上位を抽出)
  type Agg = { name: string; category: string; actual: number; budget: number; prev: number };
  const byAccount = new Map<string, Agg>();
  for (const r of dedupedRows) {
    const name = (r.account_name as string) || (r.account_code as string) || '';
    if (!name) continue;
    // 役員関連は会計科目分析の対象外 (勘定科目「役員報酬」/ 補助科目に「社長清算」「役員」を含むもの)。
    // 全社サマリー等の総額には引き続き含めるため、ここ (異常科目集計) でのみ除外する。
    const subName = (r.sub_account_name as string) || '';
    if (name.includes('役員報酬') || subName.includes('社長清算') || subName.includes('役員')) continue;
    const key = (r.account_code as string) || name;
    const cur = byAccount.get(key) ?? { name, category: (r.category_l2_name as string) || '', actual: 0, budget: 0, prev: 0 };
    for (const m of targetMonths) {
      cur.actual += Number(r[`month_${m}_actual`]) || 0;
      cur.budget += Number(r[`month_${m}_budget`]) || 0;
      cur.prev += Number(r[`month_${m}_actual_prev`]) || 0;
    }
    byAccount.set(key, cur);
  }
  // 実績モードは「予算差・前年差」、計画モードは「計画 vs 前年差」で逸脱スコアを採る。
  // いずれも10万円超だけを候補にする (ノイズ除去)。
  const anomalies = Array.from(byAccount.values())
    .map((a) => {
      const diffBudget = a.actual - a.budget;
      const diffPrev = a.actual - a.prev;
      const planVsPrev = a.budget - a.prev; // 計画と前年の差 (計画分析モード用)
      return {
        name: a.name,
        category: a.category,
        actual: Math.round(a.actual),
        budget: Math.round(a.budget),
        prev: Math.round(a.prev),
        diffBudget: Math.round(diffBudget),
        diffPrev: Math.round(diffPrev),
        planVsPrev: Math.round(planVsPrev),
        // 全社売上に対する規模感 (材料性%)。実績モードは実績÷実績売上、計画モードは予算÷予算売上
        pctOfSales: mode === 'plan' ? pct(a.budget, sales.budget) : pct(a.actual, sales.actual),
        score: mode === 'plan' ? Math.abs(planVsPrev) : Math.max(Math.abs(diffBudget), Math.abs(diffPrev)),
      };
    })
    .filter((a) => a.score > 100000)
    .sort((a, b) => b.score - a.score)
    .slice(0, 20)
    .map(({ score, ...rest }) => rest);

  // ④ 従業員数 推移・増減 (在籍数は対象範囲末月のストック値、入退社は範囲内のフロー合算)
  // StaffHeadcountSection と同じく支店/部署で排他のため単純合算で全社計になる
  const sumStaff = (key: string): number =>
    staffRows.reduce((s, r) => s + (Number(r[key]) || 0), 0);
  const endMonth = targetMonths[targetMonths.length - 1] ?? currentMonth;
  const headcount = sumStaff(`month_${endMonth}_headcount`);
  const headcountPrev = sumStaff(`month_${endMonth}_headcount_prev`);
  const rangeHires = targetMonths.reduce((s, m) => s + sumStaff(`month_${m}_hires`), 0);
  const rangeLeaves = targetMonths.reduce((s, m) => s + sumStaff(`month_${m}_leaves`), 0);
  const staffSummary =
    staffRows.length === 0
      ? null
      : {
          asOfMonth: endMonth, // 在籍数の基準月 (対象範囲の末月)
          headcount, // 基準月末の在籍数
          headcountPrev, // 前年同月末の在籍数
          headcountYoyRate: yoyRate(headcount, headcountPrev),
          hires: rangeHires, // 対象範囲の入社数
          leaves: rangeLeaves, // 対象範囲の退職数
          net: rangeHires - rangeLeaves, // 対象範囲の純増減
        };

  // ④' 採用計画 予実 (計画採用/計画在籍と実績の差)。計画在籍は期首在籍 + 9月からの計画採用累積。
  // 計画が未保存 (通期計画採用0) の場合や従業員データが無い場合は評価対象としない (null)。
  const planHiresByMonth = input.planHiresByMonth || {};
  const yearPlanHires = MONTH_ORDER.reduce((s, m) => s + (planHiresByMonth[m] || 0), 0);
  const recruitmentPlan =
    !staffSummary || yearPlanHires === 0
      ? null
      : (() => {
          // 期首在籍 = 9月実績在籍 − 9月純増減 (期首=9月開始前のストック)
          const periodStartHeadcount =
            sumStaff('month_9_headcount') - (sumStaff('month_9_hires') - sumStaff('month_9_leaves'));
          const endIdx = MONTH_ORDER.indexOf(endMonth);
          const cumPlanHiresToEnd = MONTH_ORDER.slice(0, endIdx + 1).reduce(
            (s, m) => s + (planHiresByMonth[m] || 0),
            0,
          );
          const plannedHeadcount = periodStartHeadcount + cumPlanHiresToEnd; // 計画在籍 (基準月末)
          const rangePlanHires = targetMonths.reduce((s, m) => s + (planHiresByMonth[m] || 0), 0);
          return {
            versionName: input.planVersionName || null,
            asOfMonth: endMonth,
            plannedHeadcount, // 計画在籍 (基準月末)
            actualHeadcount: headcount, // 実績在籍 (基準月末)
            headcountDiff: headcount - plannedHeadcount, // 在籍 予実差異 (実績 − 計画)
            rangePlanHires, // 対象範囲の計画採用
            rangeActualHires: rangeHires, // 対象範囲の実績入社
            hiresDiff: rangeHires - rangePlanHires, // 採用 予実差異 (実績 − 計画)
            yearPlanHires, // 通期の計画採用合計
          };
        })();

  // ⑤ 生産性 (一人当たり指標)。財務実績を在籍数で割る労働生産性。
  // 在籍数は基準月末のストック、財務は対象範囲の累計のため、複数月範囲では概算 (前年も同条件で比較)。
  const perHead = (amount: number, count: number): number | null =>
    count > 0 ? Math.round(amount / count) : null;
  const productivity =
    !staffSummary || headcount <= 0
      ? null
      : {
          headcount,
          headcountPrev,
          salesPerHead: perHead(sales.actual, headcount),
          grossPerHead: perHead(gross.actual, headcount),
          opPerHead: perHead(op.actual, headcount),
          salesPerHeadPrev: perHead(sales.prev, headcountPrev),
          grossPerHeadPrev: perHead(gross.prev, headcountPrev),
          opPerHeadPrev: perHead(op.prev, headcountPrev),
          // 一人当たり指標の前年比 (当年一人当たり / 前年一人当たり × 100)
          salesPerHeadYoyRate:
            headcountPrev > 0 ? yoyRate(sales.actual / headcount, sales.prev / headcountPrev) : null,
          grossPerHeadYoyRate:
            headcountPrev > 0 ? yoyRate(gross.actual / headcount, gross.prev / headcountPrev) : null,
          opPerHeadYoyRate:
            headcountPrev > 0 ? yoyRate(op.actual / headcount, op.prev / headcountPrev) : null,
        };

  // ⑥ 月次推移 (会計月ごとの全社4指標)。実績評価では実績の勢い・計画乖離・前年比の転換、
  //    計画分析では budget 系列の季節性を AI が読めるよう、月単位の actual/budget/prev を渡す。
  //    実績モードは期首(9月)〜当月の実績トラジェクトリ、計画モードは通期(12ヶ月)の計画配分を出す。
  const monthTotal = (
    category: string,
    m: number,
    suffix: 'actual' | 'budget' | 'actual_prev' = 'actual',
  ): number =>
    dedupedRows
      .filter((r) => r.category_l2_name === category)
      .reduce((s, r) => s + (Number(r[`month_${m}_${suffix}`]) || 0), 0);

  const monthIdx = MONTH_ORDER.indexOf(currentMonth);
  const trendMonths =
    mode === 'plan' || monthIdx < 0 ? MONTH_ORDER : MONTH_ORDER.slice(0, monthIdx + 1);
  const monthlyTrend = trendMonths.map((m) => {
    const ms = { actual: monthTotal('純売上高', m), budget: monthTotal('純売上高', m, 'budget'), prev: monthTotal('純売上高', m, 'actual_prev') };
    const mc = { actual: monthTotal('完成工事原価', m), budget: monthTotal('完成工事原価', m, 'budget'), prev: monthTotal('完成工事原価', m, 'actual_prev') };
    const mg = { actual: monthTotal('販売費及び一般管理費計', m), budget: monthTotal('販売費及び一般管理費計', m, 'budget'), prev: monthTotal('販売費及び一般管理費計', m, 'actual_prev') };
    // 売上総利益 = 売上 - 原価、営業利益 = 売上総利益 - 販管費
    const triplet = (s: number, c: number, g: number) => ({ sales: s, gross: s - c, sga: g, op: s - c - g });
    const a = triplet(ms.actual, mc.actual, mg.actual);
    const b = triplet(ms.budget, mc.budget, mg.budget);
    const p = triplet(ms.prev, mc.prev, mg.prev);
    return {
      month: m,
      sales: { actual: Math.round(a.sales), budget: Math.round(b.sales), prev: Math.round(p.sales) },
      grossProfit: { actual: Math.round(a.gross), budget: Math.round(b.gross), prev: Math.round(p.gross) },
      sga: { actual: Math.round(a.sga), budget: Math.round(b.sga), prev: Math.round(p.sga) },
      operatingProfit: { actual: Math.round(a.op), budget: Math.round(b.op), prev: Math.round(p.op) },
    };
  });

  // ⑦ 実績の利益構造比率 (当期・前年)。planRatios の実績版で、利益率の前年比較に使う。
  const actualRatios = {
    grossMarginRate: pct(gross.actual, sales.actual), // 売上総利益率(実績)
    sgaRate: pct(sga.actual, sales.actual), // 販管費率(実績)
    opMarginRate: pct(op.actual, sales.actual), // 営業利益率(実績)
    grossMarginRatePrev: pct(gross.prev, sales.prev), // 売上総利益率(前年)
    sgaRatePrev: pct(sga.prev, sales.prev), // 販管費率(前年)
    opMarginRatePrev: pct(op.prev, sales.prev), // 営業利益率(前年)
  };

  const summary = {
    fiscalYear,
    periodLabel: `${fiscalYear}年度 ${rangeLabel(performanceRange, currentMonth)}`,
    unit: 'JPY',
    mode, // 'actual'=実績評価 / 'plan'=計画(予算)分析 (未到来の決算期、または実績ゼロで予算のみの期)
    planRatios, // 計画ベースの利益構造比率 (計画分析モードで主に使う)
    actualRatios, // 実績ベースの利益構造比率 (当期・前年)
    companySummary,
    branches, // 拠点別の売上高 (規模順)
    departments, // 部署グループ別の売上高 (規模順)
    anomalies,
    monthlyTrend, // 会計月ごとの全社4指標の推移 (actual/budget/prev)
    staffSummary,
    recruitmentPlan, // 採用計画 予実 (計画採用/計画在籍と実績の差。計画未保存時は null)
    productivity,
  };
  // マトリクス表は実績評価モードのみ (計画分析モードの見出し構成には表の差し込み先が無い)
  const matrices: SalesMatrices =
    mode === 'actual'
      ? { branch: buildSalesMatrix('拠点', branchUnits), department: buildSalesMatrix('部署', departmentUnits) }
      : { branch: null, department: null };
  return { summary, matrices };
}

/**
 * AI 評価分析コメントの取得フック。generate(input) で評価を生成し、結果は result(Markdown) に入る。
 */
export function useProfitAndLossReview() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);

  const generate = useCallback(async (input: PlReviewInput) => {
    setLoading(true);
    setError(null);
    try {
      const token = await getIdToken();
      if (!token) {
        setError('認証情報を取得できませんでした。再ログインしてください。');
        return;
      }
      const { summary, matrices } = buildSummary(input);
      const res = await axios.post(
        PL_REVIEW_URL,
        { summary },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.data?.status === 'success' && typeof res.data.reply === 'string') {
        setResult(injectSalesMatrices(res.data.reply, matrices));
      } else {
        setError(res.data?.message || 'AI 評価の生成に失敗しました');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'AI 評価の生成中にエラーが発生しました');
    } finally {
      setLoading(false);
    }
  }, []);

  const reset = useCallback(() => {
    setResult(null);
    setError(null);
  }, []);

  return { loading, error, result, generate, reset };
}
